import React, { useCallback, useEffect, useMemo } from 'react';
import {
  BrowserRouter as Router,
  Route,
  Switch,
  RouteProps,
  Redirect,
  useHistory
} from 'react-router-dom';
import { InteractionType, InteractionStatus } from '@azure/msal-browser';
import { MsalAuthenticationResult, MsalAuthenticationTemplate, useMsal } from '@azure/msal-react';
import CircularProgress from 'components/@common/CircularProgress';
import BulkImportUploadModule from 'components/BulkImportUploadModule';
import PrintScreenModule from 'components/PrintScreenModule';
import WelcomePage from 'components/Welcome';
import { getLoginRequest } from 'config/auth';
import useNavigationUserSetting from 'graph/hooks/use-navigation-user-setting';
import { getAppRootRoutes, getUnauthenticatedRoutes } from 'routes';
import useStore from 'store';
import { RouteConfig } from 'types';
import { QueryParamProvider } from 'use-query-params';
import { hasAuthCookies } from 'utilities/auth-cookie-util';
import { shouldShowOnboardingWelcomeUser } from 'utilities/user-setttings-util';
import ErrorPage from '../../Error';
import LayoutContainer from '../LayoutContainer';
import MainLayout from '../MainLayout';

const APP_ROOT_ROUTES = getAppRootRoutes();
const UNAUTHENTICATED_ROUTES = getUnauthenticatedRoutes();

const USER_CANCELLED_AUTH_CODE = 'AADB2C90091';

const ErrorRedirect: React.FC<MsalAuthenticationResult> = ({ error }) => {
  if (!error || error.errorMessage?.includes(USER_CANCELLED_AUTH_CODE)) {
    window.location.replace('/');
    return null;
  }

  return <div>An error occurred: {error.errorMessage}</div>;
};

const getSidebarSetting = ({
  isDesktop,
  userSidebarSetting
}: {
  isDesktop: boolean;
  userSidebarSetting: boolean | null;
}): boolean => {
  if (isDesktop) {
    if (userSidebarSetting !== null && userSidebarSetting !== undefined) return userSidebarSetting;
    return true;
  }

  return false;
};

const registerPageView = (path: string): void => {
  try {
    const dataLayer = window.dataLayer || [];
    dataLayer.push({ event: 'Pageview', pagePath: path });
  } catch {
    return;
  }
};

const withRoutePage = ({
  id,
  path,
  Component,
  AppBar,
  NavBar,
  ...appRouteConfigProps
}: RouteConfig) => (props: RouteProps) => {
  registerPageView(path);

  return (
    <LayoutContainer AppBar={AppBar} NavBar={NavBar} id={id} path={path} {...appRouteConfigProps}>
      <Component {...props} id={id} path={path} {...appRouteConfigProps} />
    </LayoutContainer>
  );
};

const withAuth = (element: React.ReactElement): React.ReactElement => {
  if (hasAuthCookies()) return element;

  return (
    <MsalAuthenticationTemplate
      interactionType={InteractionType.Redirect}
      authenticationRequest={getLoginRequest()}
      errorComponent={ErrorRedirect}
    >
      {element}
    </MsalAuthenticationTemplate>
  );
};

const MainLayoutRouter = (): React.ReactElement => {
  const userSidebarSetting: boolean | null = null; // placeholder for a user desktop setting
  const [{ ui, userPrivilegesCache, user }, { setNavigationDrawer }] = useStore();
  const [, { updateLeftNavigationSetting }] = useNavigationUserSetting();
  const history = useHistory();

  const toggleDrawer = useCallback(async () => {
    updateLeftNavigationSetting(!ui.navigationDrawerOpen);
    setNavigationDrawer(!ui.navigationDrawerOpen);
  }, [ui.navigationDrawerOpen, setNavigationDrawer, updateLeftNavigationSetting]);

  useEffect(() => {
    setNavigationDrawer(getSidebarSetting({ isDesktop: ui.isDesktop, userSidebarSetting }));
  }, [ui.isDesktop, setNavigationDrawer]);

  useEffect(() => {
    if (shouldShowOnboardingWelcomeUser(user?.me)) {
      history.push('/welcome', {
        redirect: { pathname: history.location.pathname, search: history.location.search }
      });
    }
  }, [history, user]);

  const routes = useMemo(() => {
    const isAuthenticated = !!user?.me;

    return (
      <Switch>
        {APP_ROOT_ROUTES.filter(
          // TODO: Kind of backwards, but include route if not authenticated so that the
          // MSAL redirect after authenticating will go to originally-intended route.

          // Might be cleaner to wrap the entire MainLayoutRouter with the auth check, but
          // this led to additional renders so doing it this way for now.
          x => !isAuthenticated || userPrivilegesCache.isPrivileged(x.privileges)
        ).map(x => (
          <Route key={x.id} path={x.path} render={props => withAuth(withRoutePage(x)(props))} />
        ))}
        <Route key="error" id="error" path="/error" component={ErrorPage} />
        <Route key="default" id="default">
          <Redirect to="dashboard" />
        </Route>
      </Switch>
    );
  }, [user, userPrivilegesCache]);

  if (user.isLoading) return <CircularProgress loading />;
  const isDrawerOpen = ui.navigationDrawerOpen === null ? ui.isDesktop : ui.navigationDrawerOpen;

  return (
    <MainLayout toggleDrawer={toggleDrawer} isDrawerOpen={isDrawerOpen} desktopView={ui.isDesktop}>
      {routes}
    </MainLayout>
  );
};

const AppRouter: React.FC = () => {
  const { accounts, inProgress } = useMsal();

  const unauthenticatedRoutes = useMemo(() => {
    if (accounts.length > 0) return [];
    return UNAUTHENTICATED_ROUTES.map(x => (
      <Route key={x.id} path={x.path} render={props => withRoutePage(x)(props)} />
    ));
  }, [accounts]);

  if (inProgress !== InteractionStatus.None) return <CircularProgress loading />;

  return (
    <React.Fragment>
      <Router>
        <QueryParamProvider ReactRouterRoute={Route}>
          <Switch>
            {unauthenticatedRoutes}
            {/* These routes are screens which don't render the default header/footer/sidebar */}
            <Route path="/upload" component={BulkImportUploadModule} />
            <Route
              path="/print-screen"
              render={props => withAuth(<PrintScreenModule {...(props as any)} />)}
            />
            <Route
              path="/welcome"
              render={props => withAuth(<WelcomePage {...(props as any)} />)}
            />
            {/* Routes with the standard header/footer/sidebar layout */}
            <Route component={MainLayoutRouter} />
          </Switch>
        </QueryParamProvider>
      </Router>
    </React.Fragment>
  );
};

export default AppRouter;
